'use client';

import { Image } from '@ahafood/ui/components/image';
import { cn } from '@ahm/common-helpers';
import { FreeDragCarousel } from '@/components/carousel/free-drag-carousel';
import { Link } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';

interface QuickSearchProps {
  citySlug: string;
}

interface LabelSegment {
  text: string;
  color: string;
}

interface QuickSearchItem {
  id: string;
  testId: string;
  labelLines: LabelSegment[][];
  image: string;
  href: (citySlug: string) => string;
  eventName: string;
}

const quickSearchItems: QuickSearchItem[] = [
  {
    id: 'nearby',
    testId: 'home-quick-recent',
    labelLines: [[{ text: 'Gần đây', color: 'text-[#5E6C84]' }]],
    image: '/static/images/quick-action5.webp',
    href: (citySlug: string) => `/${citySlug}/danh-sach-cua-hang-gan-toi`,
    eventName: 'nearby',
  },
  {
    id: 'solomeal',
    testId: 'home-quick-solomeal',
    labelLines: [
      [
        { text: 'Solo', color: 'text-[#0D4073]' },
        { text: 'Meal', color: 'text-[#FF7F32]' },
      ],
    ],
    image: '/static/images/solomeal-quick-search.webp',
    href: (citySlug: string) => `/${citySlug}/solomeal`,
    eventName: 'solomeal',
  },
  {
    id: 'ai-suggest',
    testId: 'home-quick-for-u',
    labelLines: [
      [{ text: 'AI', color: 'text-[#0089FF]' }],
      [{ text: 'gợi ý cho bạn', color: 'text-[#5E6C84]' }],
    ],
    image: '/static/images/quick-action3.webp',
    href: (citySlug: string) => `/${citySlug}/danh-sach-cua-hang-cho-ban`,
    eventName: 'ai_suggest',
  },
  {
    id: 'flashdeal',
    testId: 'home-quick-deal',
    labelLines: [
      [
        { text: 'Flash', color: 'text-[#EC221F]' },
        { text: 'Deal', color: 'text-[#FFCB05]' },
      ],
    ],
    image: '/static/images/quick-action2.webp',
    href: (citySlug: string) => `/${citySlug}/danh-sach-cua-hang-co-hot-deal`,
    eventName: 'flashdeal',
  },
  {
    id: 'new-stores',
    testId: 'home-quick-new-store',
    labelLines: [[{ text: 'Quán mới', color: 'text-[#5E6C84]' }]],
    image: '/static/images/quick-action1.webp',
    href: (citySlug: string) => `/${citySlug}/danh-sach-cua-hang-moi`,
    eventName: 'new_stores',
  },
  {
    id: 'trending',
    testId: 'home-quick-trending',
    labelLines: [[{ text: 'Xu hướng', color: 'text-[#5E6C84]' }]],
    image: '/static/images/quick-action4.webp',
    href: (citySlug: string) => `/${citySlug}/danh-sach-cua-hang-xu-huong`,
    eventName: 'trending',
  },
];

export default function QuickSearch({ citySlug }: QuickSearchProps) {
  const handleClick = (eventName: string, label: string) => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: eventName,
      button_label: label,
      city_slug: citySlug,
      event_type: 'quick_search',
      event_date: new Date().toISOString().split('T')[0],
      event_timestamp: Math.floor(Date.now() / 1000),
    });
  };

  return (
    <div className="w-full bg-transparent pt-4 pb-6">
      {/* Mobile: Snap carousel — edge-to-edge bleed */}
      <div className="lg:hidden">
        <FreeDragCarousel
          opts={{ loop: false, containScroll: 'trimSnaps' }}
          contentClassName="ml-0"
        >
          {quickSearchItems.map((item, idx) => (
            <FreeDragCarousel.Item
              key={item.id}
              className={cn(
                idx === 0 ? 'basis-[128px] pl-4' : 'basis-[124px] pl-3',
                idx === quickSearchItems.length - 1 && 'basis-[140px] pr-4'
              )}
            >
              <Link
                href={item.href(citySlug)}
                onClick={() =>
                  handleClick(
                    item.eventName,
                    item.labelLines
                      .flat()
                      .map((s) => s.text)
                      .join('')
                  )
                }
                className={cn(
                  'group relative block size-28 aspect-square rounded-[24px] p-[1.5px]',
                  'bg-gradient-to-b from-[#FF7F32] via-[#FF7F32]/50 to-[#0089FF]',
                  'transition-transform duration-200 active:scale-95'
                )}
              >
                <div className="flex size-full flex-col items-center rounded-[22.5px] bg-white px-2 pt-3 shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-shadow group-hover:shadow-[0_6px_16px_rgba(0,0,0,0.1)]">
                  <div className="flex h-[38px] flex-col items-center justify-center text-center text-[14px] font-bold leading-[1.1]">
                    {item.labelLines.map((line, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-center"
                      >
                        {line.map((segment, sIdx) => (
                          <span key={sIdx} className={cn(segment.color)}>
                            {segment.text}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="relative mt-2 flex-1 w-full scale-110 mb-1">
                    <Image
                      src={item.image}
                      alt={item.id}
                      fill
                      className="object-contain object-bottom"
                      sizes="100px"
                    />
                  </div>
                </div>
              </Link>
            </FreeDragCarousel.Item>
          ))}
        </FreeDragCarousel>
      </div>

      {/* Desktop: 2x3 Grid (giữ container max-width + padding) */}
      <div className="container mx-auto hidden lg:grid lg:grid-cols-3 gap-4 max-w-screen-xl px-4 md:px-6">
        {quickSearchItems.map((item) => (
          <Link
            key={item.id}
            href={item.href(citySlug)}
            onClick={() =>
              handleClick(
                item.eventName,
                item.labelLines
                  .flat()
                  .map((s) => s.text)
                  .join('')
              )
            }
            className={cn(
              'group relative h-32 rounded-[24px] p-[1.5px] border border-white/80',
              // 'bg-gradient-to-b from-[#FF7F32] via-[#FF7F32]/50 to-[#0089FF]',
              'transition-all duration-200 hover:scale-105 hover:shadow-lg active:scale-95'
            )}
          >
            <div className="flex size-full items-center gap-4 rounded-[22.5px] bg-white/50 px-6 py-4 shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-shadow group-hover:shadow-[0_8px_20px_rgba(0,0,0,0.12)]">
              {/* Label */}
              <div className="flex flex-col justify-center text-left font-bold leading-tight">
                {item.labelLines.map((line, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    {line.map((segment, sIdx) => (
                      <span key={sIdx} className={cn(segment.color, 'text-lg')}>
                        {segment.text}
                      </span>
                    ))}
                  </div>
                ))}
              </div>

              {/* Illustration */}
              <div className="relative ml-auto size-20 shrink-0">
                <Image
                  src={item.image}
                  alt={item.id}
                  fill
                  className="object-contain"
                  sizes="80px"
                />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
